import { useState } from 'react';
import { ChevronRight, Download, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  GlassCard,
  SettingsPageHeader,
  SurfacePageBody,
  SurfacePageLayout,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { ToggleSwitch } from './ToggleSwitch';
import { SEED_SHARED_CHATS } from '@/versions/v1/shared/settings-seed';

export function PrivacyScene() {
  const [analyticsOn, setAnalyticsOn] = useState(true);

  return (
    <SurfacePageLayout width="narrow">
      <Commentable name="PrivacyScene">
        <div className="contents">
          <SurfacePageBody className="space-y-8" as="div">
          <SettingsPageHeader
            title="Privacy"
            description="What we store, what we don't, and how to take it with you."
          />

          <Commentable name="Intro">
            <GlassCard as="section" tone="strong" className="overflow-hidden">
            <div className="px-5 py-5">
              <p className="text-sm leading-relaxed text-zinc-700 dark:text-zinc-300">
                We keep what&apos;s necessary to make the assistant work for you.
                Your conversations, memories, connectors, and skills are stored and
                tied to your account.
              </p>
              <ul className="ml-4 mt-4 list-disc space-y-1.5 text-sm leading-relaxed text-zinc-700 dark:text-zinc-300">
                <li>Data is scoped to your account; no cross-tenant reads.</li>
                <li>Connector secrets are stored as-typed and never logged.</li>
                <li>
                  Product analytics are sent only when the toggle below is on.
                  Operational logs are always collected for reliability.
                </li>
              </ul>
              <div className="mt-4 space-y-2">
                <CalloutDetails title="How we protect your data">
                  Encryption in transit and at rest, scoped access controls, and
                  regular security reviews.
                </CalloutDetails>
                <CalloutDetails title="How we use your data">
                  To run the assistant, improve reliability, and — when opted in —
                  product analytics.
                </CalloutDetails>
              </div>
            </div>
            </GlassCard>
          </Commentable>

          <Commentable name="Preferences">
            <section>
              <h2 className="mb-3 text-base font-medium text-zinc-900 dark:text-zinc-100">
                Preferences
              </h2>
              <GlassCard tone="strong" className="overflow-hidden">
                <div className="flex items-start justify-between gap-6 px-5 py-4">
                  <div className="min-w-0 flex-1">
                    <label
                      htmlFor="product-analytics"
                      className="block font-medium text-zinc-900 dark:text-zinc-100"
                    >
                      Product analytics
                    </label>
                    <p className="mt-1 text-sm text-muted">
                      Help us improve by sharing anonymous product usage. Errors and
                      operational logs are always collected for reliability.
                      Disabling this stops product event tracking.
                    </p>
                  </div>
                  <ToggleSwitch
                    id="product-analytics"
                    checked={analyticsOn}
                    onCheckedChange={setAnalyticsOn}
                  />
                </div>
              </GlassCard>
            </section>
          </Commentable>

          <Commentable name="YourData">
            <section>
              <h2 className="mb-3 text-base font-medium text-zinc-900 dark:text-zinc-100">
                Your data
              </h2>
              <GlassCard tone="strong" className="overflow-hidden">
                <DataRow
                  label="Export data"
                  description="Download all your conversations, memories, connectors, and skills as a single JSON file."
                  action={
                    <button
                      type="button"
                      className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-[var(--glass-border-strong)] bg-[var(--glass-surface-strong)] px-3 text-sm font-medium text-zinc-900 transition-colors hover:bg-[var(--glass-surface)] dark:text-zinc-100"
                    >
                      <Download className="h-3.5 w-3.5" />
                      Export data
                    </button>
                  }
                />
                <hr className="border-t border-[var(--glass-border)]" />
                <DataRow
                  label="Memory preferences"
                  description="Manage what the assistant remembers about you across conversations."
                  action={
                    <Link
                      to="/settings/memory"
                      className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-[var(--glass-border-strong)] bg-[var(--glass-surface-strong)] px-3 text-sm font-medium text-zinc-900 transition-colors hover:bg-[var(--glass-surface)] dark:text-zinc-100"
                    >
                      Manage
                      <ExternalLink className="h-3.5 w-3.5" />
                    </Link>
                  }
                />
              </GlassCard>
            </section>
          </Commentable>

          <Commentable name="SharedChats">
            <section>
              <h2 className="mb-3 text-base font-medium text-zinc-900 dark:text-zinc-100">
                Shared chats
              </h2>
              <p className="mb-3 text-sm text-muted">
                Conversations you have shared with teammates.
              </p>
              <GlassCard tone="strong" className="overflow-hidden">
                {SEED_SHARED_CHATS.map((share, index) => (
                  <div
                    key={share.id}
                    className={
                      index > 0
                        ? 'flex items-start justify-between gap-4 border-t border-[var(--glass-border)] px-5 py-4'
                        : 'flex items-start justify-between gap-4 px-5 py-4'
                    }
                  >
                    <div>
                      <div className="font-medium text-zinc-900 dark:text-zinc-100">
                        {share.title}
                      </div>
                      <div className="mt-1 text-sm text-muted">
                        Shared with {share.sharedWith}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        className="rounded-lg border border-[var(--glass-border-strong)] px-3 py-1.5 text-sm"
                      >
                        Open
                      </button>
                      <button
                        type="button"
                        className="rounded-lg border border-red-200 px-3 py-1.5 text-sm text-red-700 dark:border-red-900/60 dark:text-red-400"
                      >
                        Revoke
                      </button>
                    </div>
                  </div>
                ))}
              </GlassCard>
            </section>
          </Commentable>
          </SurfacePageBody>
        </div>
      </Commentable>
    </SurfacePageLayout>
  );
}

function CalloutDetails({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <details className="rounded-lg border border-[var(--glass-border)] bg-[var(--glass-surface-soft)] px-4 py-3">
      <summary className="flex cursor-pointer list-none items-center justify-between text-sm font-medium text-zinc-900 dark:text-zinc-100 [&::-webkit-details-marker]:hidden">
        {title}
        <ChevronRight className="h-4 w-4 text-zinc-400 transition-transform [[open]>&]:rotate-90" />
      </summary>
      <p className="mt-2 text-sm text-muted">{children}</p>
    </details>
  );
}

function DataRow({
  label,
  description,
  action,
}: {
  label: string;
  description: string;
  action: React.ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-6 px-5 py-4">
      <div className="min-w-0 flex-1">
        <div className="font-medium text-zinc-900 dark:text-zinc-100">{label}</div>
        <p className="mt-1 text-sm text-muted">{description}</p>
      </div>
      <div className="flex-shrink-0">{action}</div>
    </div>
  );
}
